import { ConnectivityTable } from "@/features/Connectors/ConnectivityTable";
import { JumpToSection } from "./JumpToSection";
import { ComingSoonSection } from "./ComingSoonSection";
import { Toggle } from "@/ui/atoms/Toggle/Toggle";
import { useState } from "react";
import { DataSource, useColorizedValue } from "../hooks/useColorizedValue";
import { useFlags } from "@/utils/flagsmith/useFlags";
import clsx from "clsx";
import { LabelLLM } from "./LabelLLM";
import { InvestmentCriteriaTable } from "./InvestmentCriteriaTable";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { useSetAtom } from "jotai";
import { showInvestmentCriteriaDrawerAtom } from "../state";
import { rightDrawerOpenAtom } from "@/features/Navigation/state";
import { useApiQuery } from "@/api";
import { FinanceLabField, getFinanceLabMetrics } from "@/entities/FinanceLab";
import { ExpandableMarkdown } from "./ExpandableMarkdown";

export function DueDiligence({
    companyId,
    opportunityId,
}: {
    companyId: string;
    opportunityId?: string;
}) {
    const [showActiveOnly, setShowActiveOnly] = useState(false);
    const { data: backgroundCheckMetrics } = useApiQuery(getFinanceLabMetrics, {
        params: {
            labels: [FinanceLabField.ORGANIZATION_BACKGROUND_CHECK],
        },
    });
    const flags = useFlags();
    const showDataSources = flags.enabled("show-data-sources");

    const setShowInvestmentCriteriaDrawer = useSetAtom(
        showInvestmentCriteriaDrawerAtom,
    );
    const setRightDrawerOpen = useSetAtom(rightDrawerOpenAtom);

    const handleViewDetails = () => {
        setShowInvestmentCriteriaDrawer(true);
        setRightDrawerOpen(true);
    };

    const backgroundCheckValue = backgroundCheckMetrics?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.ORGANIZATION_BACKGROUND_CHECK,
    )?.values;

    const backgroundCheckColorized = useColorizedValue(
        backgroundCheckValue?.[0]?.formattedValue || "-",
        DataSource.FINANCE_LAB,
        FinanceLabField.ORGANIZATION_BACKGROUND_CHECK,
    );

    return (
        <>
            <div className="space-y-6">
                <div>
                    <div className="mb-4 flex items-center justify-between">
                        <h3 className="heading-m">Investment Criteria</h3>
                        <span
                            role="button"
                            onClick={handleViewDetails}
                            className={clsx(
                                "group",
                                "text-link",
                                "flex cursor-pointer items-center gap-2",
                            )}
                        >
                            View Details
                            <ChevronRightIcon className="size-4 stroke-brand-300 group-hover:stroke-brand-400" />
                        </span>
                    </div>
                    <InvestmentCriteriaTable opportunityId={opportunityId} />
                </div>
                <div className="mt-6">
                    <div className="mb-4 flex items-center justify-between">
                        <h4 className="heading-m">Connectivity</h4>
                        <div className="flex items-center gap-3">
                            <span className="text-sm text-slate-200">
                                Show Active Only
                            </span>
                            <Toggle
                                checked={showActiveOnly}
                                onChange={setShowActiveOnly}
                                testId="show-active-only-toggle"
                            />
                        </div>
                    </div>
                    <ConnectivityTable
                        companyId={companyId}
                        showActiveOnly={showActiveOnly}
                    />
                </div>
                <ComingSoonSection title="Cap Table" />
                <ComingSoonSection title="Sources & Uses" />
                <ComingSoonSection title="Corporate Structure" />
                <ComingSoonSection title="Lien Search" />
                <div className="mt-6">
                    <h3 className="heading-m mb-3">Background Checks</h3>
                    <div>
                        <div className="mb-2">
                            <ExpandableMarkdown
                                text={backgroundCheckColorized.value}
                                maxLength={1800}
                            />
                        </div>
                        {showDataSources && backgroundCheckColorized.source && (
                            <span
                                className={clsx(
                                    "mt-1 text-[9px] font-medium tracking-wide",
                                    backgroundCheckColorized.sourceClassName,
                                )}
                            >
                                {backgroundCheckColorized.source}
                            </span>
                        )}
                        <LabelLLM />
                    </div>
                </div>
            </div>
            <JumpToSection
                targetSection="capital-and-deal-structure"
                label="Jump to Capital & Deal Structure"
            />
        </>
    );
}
